import React from 'react';
import { Box, Container, Typography, Grid, Button, IconButton } from '@mui/material';
import { motion } from 'framer-motion';
import heroImage from '../assets/hero-image.png';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

const Hero = () => {
  const scrollToNext = () => {
    const nextSection = document.getElementById('next-section');
    if (nextSection) {
      nextSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.3
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        ease: "easeOut"
      }
    }
  };

  const imageVariants = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: {
        duration: 1,
        ease: "easeOut"
      }
    }
  };

  return (
    <Box
      component={motion.div}
      initial="hidden"
      animate="visible"
      variants={containerVariants}
      sx={{
        background: 'linear-gradient(135deg, #f5f5f5 0%, #e0e0e0 100%)',
        minHeight: '90vh',
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '100%',
          background: 'linear-gradient(45deg, rgba(27, 94, 32, 0.05) 0%, transparent 100%)',
          zIndex: 1
        }
      }}
    >
      <Container
        maxWidth="lg"
        sx={{
          position: 'relative',
          zIndex: 2,
          py: { xs: 8, md: 12 },
          minHeight: '80vh',
          display: 'flex',
          alignItems: 'center'
        }}
      >
        <Grid container spacing={6} alignItems="center">
          <Grid item xs={12} md={7}>
            <Box component={motion.div} variants={itemVariants}>
              <Box sx={{ mb: 4 }}>
                <Typography
                  variant="h3"
                  component="h1"
                  sx={{
                    color: '#1B5E20',
                    fontWeight: 700,
                    mb: 2,
                    fontSize: { xs: '1.8rem', sm: '2.2rem', md: '2.5rem' },
                    position: 'relative',
                    display: 'inline-block',
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      bottom: -8,
                      left: 0,
                      width: '60%',
                      height: '4px',
                      background: 'linear-gradient(90deg, #1B5E20 0%, transparent 100%)',
                      borderRadius: '2px'
                    }
                  }}
                >
                  THE INDUSTRIAL SECTORS OF
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    color: '#1B5E20',
                    fontWeight: 600,
                    fontSize: { xs: '2rem', sm: '2.4rem', md: '2.8rem' },
                    background: 'linear-gradient(90deg, #1B5E20, #2E7D32)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent'
                  }}
                >
                  VIBHUTI INDUSTRIES
                </Typography>
              </Box>
              <Typography
                variant="body1"
                sx={{
                  color: '#546E7A',
                  mb: 4,
                  maxWidth: 600,
                  fontSize: { xs: '1rem', sm: '1.1rem' },
                  lineHeight: 1.8,
                  position: 'relative',
                  pl: 3,
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    left: 0,
                    top: 0,
                    width: '2px',
                    height: '100%',
                    background: 'linear-gradient(180deg, #1B5E20 0%, transparent 100%)'
                  }
                }}
              >
                Founded in 2021, Vibhuti Industries India Private Limited is a leading name in India's defense manufacturing sector, specializing in the production of high-precision components for armored vehicles, firearms, and aircraft.
              </Typography>
              <Box
                component={motion.div}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Button
                  variant="contained"
                  endIcon={<ArrowForwardIcon />}
                  sx={{
                    bgcolor: '#1B5E20',
                    color: 'white',
                    px: 4,
                    py: 1.8,
                    fontSize: '1rem',
                    textTransform: 'none',
                    borderRadius: '30px',
                    boxShadow: '0 4px 20px rgba(27, 94, 32, 0.25)',
                    position: 'relative',
                    overflow: 'hidden',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      background: 'linear-gradient(45deg, transparent, rgba(255,255,255,0.2), transparent)',
                      transform: 'translateX(-100%)',
                      transition: 'transform 0.6s'
                    },
                    '&:hover': {
                      bgcolor: '#2E7D32',
                      '&::before': {
                        transform: 'translateX(100%)'
                      }
                    }
                  }}
                >
                  Learn More
                </Button>
              </Box>
            </Box>
          </Grid>
          <Grid item xs={12} md={5}>
            <Box
              component={motion.div}
              variants={imageVariants}
              whileHover={{ scale: 1.02 }}
              sx={{
                width: '100%',
                height: { xs: '300px', md: '450px' },
                position: 'relative',
                overflow: 'hidden',
                borderRadius: '16px',
                boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: 'linear-gradient(45deg, rgba(27, 94, 32, 0.1), transparent)',
                  zIndex: 1
                }
              }}
            >
              <motion.img
                src={heroImage}
                alt="Vibhuti Industries Manufacturing Facility"
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover'
                }}
                initial={{ scale: 1.2 }}
                animate={{ scale: 1 }}
                transition={{ duration: 1.5 }}
              />
            </Box>
          </Grid>
        </Grid>
      </Container>

      {/* Scroll Indicator */}
      <Box
        component={motion.div}
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.5, duration: 0.8 }}
        sx={{
          position: 'absolute',
          bottom: 40,
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 2,
          cursor: 'pointer'
        }}
        onClick={scrollToNext}
      >
        <IconButton
          component={motion.div}
          animate={{
            y: [0, 10, 0],
          }}
          transition={{
            duration: 1.5,
            repeat: Infinity,
            repeatType: "reverse"
          }}
          sx={{ color: '#1B5E20' }}
        >
          <KeyboardArrowDownIcon fontSize="large" />
        </IconButton>
      </Box>
    </Box>
  );
};

export default Hero;
